import React from 'react';
import { Helmet } from 'react-helmet-async';
import type { Course } from '../../types/course';

interface StructuredDataProps {
  courses: Course[];
  siteName?: string;
}

export default function StructuredData({ courses, siteName = 'EcoleMinds' }: StructuredDataProps): React.JSX.Element {
  const organizationData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteName,
    url: typeof window !== 'undefined' ? window.location.origin : '',
    logo: typeof window !== 'undefined' ? `${window.location.origin}/favicon.ico` : '',
    sameAs: [
      'https://linkedin.com/company/ecoleminds',
      'https://twitter.com/ecoleminds',
    ],
    description:
      'Research-led AI education for BFSI professionals. Practical skills for CAs, Bankers, FP&A, Risk & Fintech.',
  };

  const websiteData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    url: typeof window !== 'undefined' ? window.location.origin : '',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${typeof window !== 'undefined' ? window.location.origin : ''}/courses?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const courseListData = courses.slice(0, 6).map((course) => ({
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: course.title,
    description: course.short_description || course.title,
    provider: {
      '@type': 'Organization',
      name: siteName,
      sameAs: typeof window !== 'undefined' ? window.location.origin : '',
    },
    url: `${typeof window !== 'undefined' ? window.location.origin : ''}/courses/${course.slug || course.id}`,
    ...(course.price > 0
      ? {
          offers: {
            '@type': 'Offer',
            price: (course.discount_price > 0 ? course.discount_price : course.price).toString(),
            priceCurrency: 'INR',
            availability: 'https://schema.org/InStock',
          },
        }
      : {}),
  }));

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: typeof window !== 'undefined' ? window.location.origin : '',
      },
    ],
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(organizationData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbData)}
      </script>
      {courseListData.map((course, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(course)}
        </script>
      ))}
    </Helmet>
  );
}
